import { formatterEspaces } from "../../../helpers/formatters";
import {
  isActiviteAvant1Month,
  isActiviteAvantDernierBulletin,
  isDateDebutSituationPlus1an,
  isDateDebutSituationPlusOrEqual1an,
} from "../../../helpers/validators";
import {
  DossierLocation,
  DossierLocationCompositionLocationEnum,
  DossierParticulierDetail,
  DossierParticulierStatutEnum,
  SituationProfessionnelle,
  StatutProfessionnel,
} from "../../../services/Api/swagger";

export interface ISituationProfessionnellePresentation {
  situation: string;
  codeStatutProfessionnel?: string;
  dateDebutSituationProfessionnelle?: string;
  dateFinSituationProfessionnelle?: string;
  nombreMoisTravailles?: number;
  pasAvisImposition?: boolean;
  salaireNetImposableM1?: number;
  salaireNetImposableM2?: number;
  salaireNetImposableM3?: number;
  cumulSalaireNetImposableM1?: number;
  cumulSalaireNetImposableM2?: number;
  cumulSalaireNetImposableM3?: number;
  revenuIndividuelDeclare?: number;
  revenuFiscalReference?: number;
  dateDernierBulletin?: string;
  salaireBrutMensuel?: number;
  salaireNetMensuel?: number;
  codeApe?: string;
  siret?: string;
  reference?: string;
  typeCompte?: "LOCATAIRE" | "GARANT";
  isCumulsUtilises?: boolean;
  statut?: DossierParticulierStatutEnum | string;
}

export interface SituationProfessionnelleFormData {
  principal: ISituationProfessionnellePresentation;
  secondaire?: ISituationProfessionnellePresentation;
}

export interface SituationProfessionnelleFormError {
  principal: any;
  secondaire?: any;
}

export const getId = (
  typeCompte: "principal" | "secondaire",
  attribut: string
): string => typeCompte + "." + attribut;

export const construireSituationsProfessionnelles = (
  situationsProfessionnelles: SituationProfessionnelle[]
) => {
  return situationsProfessionnelles
    ? situationsProfessionnelles.map((s) => ({
        key: s.code,
        text: s.libelle,
        value: s.code,
      }))
    : [];
};

export const construireStatutsProfessionnels = (
  statutsProfessionnelles: StatutProfessionnel[]
) => {
  return statutsProfessionnelles
    ? statutsProfessionnelles.map((s) => ({
        key: s.code,
        text: s.libelle,
        value: s.code,
      }))
    : [];
};

export const situationProfessionnelleFormToModel = (
  situationProfessionnelleForm: SituationProfessionnelleFormData,
  dossierLocation: DossierLocation,
  compositionLocation?: DossierLocationCompositionLocationEnum
): SituationProfessionnelleFormData => {
  let model = {} as SituationProfessionnelleFormData;
  const { principal, secondaire } = situationProfessionnelleForm;
  const dossierLocationPrincipal =
    dossierLocation !== undefined &&
    dossierLocation.dossiersParticuliers !== undefined &&
    dossierLocation.dossiersParticuliers.find(
      (locataire) =>
        situationProfessionnelleForm &&
        locataire.reference === situationProfessionnelleForm.principal.reference
    );

  const situationPrincipaleFormattage = {
    ...principal,
    codeApe: principal.situation === "ETU" ? undefined : principal.codeApe,
    dateDernierBulletin:
      principal.situation === "ETU" ? undefined : principal.dateDernierBulletin,
    nombreMoisTravailles:
      principal.situation === "ETU"
        ? undefined
        : Number(principal.nombreMoisTravailles),
    salairebrutMensuel:
      principal.situation === "ETU" ? undefined : principal.salaireBrutMensuel,
    salaireNetMensuel:
      principal.situation === "ETU" ? undefined : principal.salaireNetMensuel,
    siret: principal.siret && formatterEspaces(principal.siret),
    statut: dossierLocationPrincipal ? dossierLocationPrincipal.statut : "",
  };
  model = {
    principal: situationPrincipaleFormattage,
  };

  if (secondaire && compositionLocation === "COUPLE") {
    const dossierLocationSecondaire =
      dossierLocation !== undefined &&
      dossierLocation.dossiersParticuliers !== undefined &&
      situationProfessionnelleForm.secondaire !== undefined &&
      dossierLocation.dossiersParticuliers.find(
        (locataire) =>
          situationProfessionnelleForm &&
          locataire.reference ===
            // @ts-ignore
            situationProfessionnelleForm.secondaire.reference
      );
    const situationConjointFormattage = {
      ...secondaire,
      nombreMoisTravailles: Number(secondaire.nombreMoisTravailles),
      siret: secondaire.siret && formatterEspaces(secondaire.siret),
      statut: dossierLocationSecondaire ? dossierLocationSecondaire.statut : "",
    };
    model = {
      ...model,
      secondaire: situationConjointFormattage,
    };
  }
  return model;
};

export const situationProfessionnelleModelToForm = (
  situationModel: SituationProfessionnelleFormData | undefined,
  listSituationProfessionnelle: SituationProfessionnelle[],
  compositionLocation?: DossierLocationCompositionLocationEnum
): SituationProfessionnelleFormData => {
  let form = {} as SituationProfessionnelleFormData;
  if (situationModel) {
    const situationPrincipal =
      situationModel.principal &&
      situationModel.principal.codeStatutProfessionnel &&
      getSituationProfessionnelleFromCodeStatut(
        situationModel.principal.codeStatutProfessionnel || "",
        listSituationProfessionnelle
      );

    form = {
      principal: {
        ...situationModel.principal,
        situation: situationPrincipal,
      },
    } as SituationProfessionnelleFormData;

    if (situationModel.secondaire && compositionLocation === "COUPLE") {
      const situationConjoint =
        situationModel.secondaire &&
        situationModel.secondaire.codeStatutProfessionnel &&
        getSituationProfessionnelleFromCodeStatut(
          situationModel.secondaire.codeStatutProfessionnel || "",
          listSituationProfessionnelle
        );
      form = {
        ...form,
        secondaire: {
          ...situationModel.secondaire,
          situation: situationConjoint || "",
        },
      };
    }
  }

  return form;
};

const getSituationProfessionnelleFromCodeStatut = (
  codeSituation: string,
  listSituationProfessionnelle: SituationProfessionnelle[]
): string => {
  const situation =
    (listSituationProfessionnelle &&
      listSituationProfessionnelle.length > 0 &&
      listSituationProfessionnelle.find(
        (situation) =>
          situation &&
          situation.statutsProfessionnels &&
          situation.statutsProfessionnels.find(
            (statut) => statut.code === codeSituation
          )
      )) ||
    undefined;

  return (situation && situation.code) || "";
};

export interface IConditionSituationPro {
  isSalarie: boolean;
  isNonSalarie: boolean;
  isRechercheEmploi: boolean;
  isDateDebutStartAt30jDateJour: boolean;
  isDateActiviteAvant45j: boolean;
  isDateActiviteAvant75j: boolean;
  isDateActiviteAvant105j: boolean;
  isDateDebutActivitePlusEqual1an: boolean;
  isDateDebutActivitePlus1an: boolean;
  isNonSalarieOrAutre: boolean;
  isCdiEssaiOrCddOrInterimaire: boolean;
  isCdiEssai: boolean;
  isCdiOrAssistanteMatOrCddOrInterimaire: boolean;
  isIntermittentRechercheEmploi: boolean;
  isEtudiantOuSansActivite: boolean;
  canSeeNombreMoisTravaille: boolean;
}

export const getConditionsAffichage = (
  form: ISituationProfessionnellePresentation | undefined
) => {
  const isSalarie =
    form && (form.situation === "SPR" || form.situation === "SPU");
  const isRechercheEmploi = form && form.situation === "RCH";
  const isNonSalarie = form && form.situation === "TNS";
  const isNonSalarieOrAutre =
    (form && form.situation === "AUT") || isNonSalarie;
  const dateDebutActivite = form && form.dateDebutSituationProfessionnelle;
  const dernierBulletinSalaire = form && form.dateDernierBulletin;
  const isDateActiviteAvant45j = isActiviteAvantDernierBulletin(
    dateDebutActivite,
    dernierBulletinSalaire,
    0
  );
  const isDateActiviteAvant75j = isActiviteAvantDernierBulletin(
    dateDebutActivite,
    dernierBulletinSalaire,
    1
  );
  const isDateActiviteAvant105j = isActiviteAvantDernierBulletin(
    dateDebutActivite,
    dernierBulletinSalaire,
    2
  );
  const isDateDebutActivitePlusEqual1an =
    isDateDebutSituationPlusOrEqual1an(dateDebutActivite);
  const isDateDebutActivitePlus1an =
    isDateDebutSituationPlus1an(dateDebutActivite);
  const isDateDebutStartAt30jDateJour =
    isActiviteAvant1Month(dateDebutActivite);

  const isCdiEssai = form?.codeStatutProfessionnel === "CDIESS";
  const isCdiEssaiOrCddOrInterimaire =
    form?.codeStatutProfessionnel === "CDIESS" ||
    form?.codeStatutProfessionnel === "INTERIM" ||
    form?.codeStatutProfessionnel === "CDD";

  const isCdiOrAssistanteMatOrCddOrInterimaire =
    isCdiEssaiOrCddOrInterimaire ||
    (form &&
      (form.codeStatutProfessionnel === "CDICONF" ||
        form.codeStatutProfessionnel === "ASSMAT"));

  const isIntermittentRechercheEmploi =
    form?.codeStatutProfessionnel === "INTERMT" || form?.situation === "RCH";

  const isEtudiantOuSansActivite =
    form?.situation === "ETU" || form?.situation === "SAC";
  const rentierOrInvalid =
    form?.codeStatutProfessionnel === "RENT" ||
    form?.codeStatutProfessionnel === "INVALD";
  const canSeeNombreMoisTravaille =
    (isSalarie || isNonSalarieOrAutre || isRechercheEmploi) &&
    !rentierOrInvalid;
  return {
    isSalarie,
    isNonSalarie,
    isRechercheEmploi,
    isDateActiviteAvant45j,
    isDateActiviteAvant75j,
    isDateActiviteAvant105j,
    isDateDebutActivitePlusEqual1an,
    isDateDebutActivitePlus1an,
    isDateDebutStartAt30jDateJour,
    isNonSalarieOrAutre,
    isCdiEssaiOrCddOrInterimaire,
    isCdiEssai,
    isCdiOrAssistanteMatOrCddOrInterimaire,
    isIntermittentRechercheEmploi,
    isEtudiantOuSansActivite,
    canSeeNombreMoisTravaille,
  } as IConditionSituationPro;
};

export const isRevenuFiscalOk = (
  form: SituationProfessionnelleFormData,
  revenuFiscal: number,
  composition: DossierLocationCompositionLocationEnum
) => {
  if (
    composition &&
    composition !== DossierLocationCompositionLocationEnum["COUPLE"]
  ) {
    const revenusIndividuels = form.principal.revenuIndividuelDeclare;

    const formule =
      revenusIndividuels &&
      Math.abs(revenuFiscal - revenusIndividuels) /
        Math.min(revenuFiscal, revenusIndividuels);
    return revenuFiscal &&
      revenuFiscal > 3 &&
      revenusIndividuels &&
      revenusIndividuels > 3 &&
      formule &&
      formule > 0.3
      ? "Nous détectons un écart important entre vos revenus imposables et vos revenus déclarés. <br />Si le revenu fiscal de référence ne correspond pas à votre situation actuelle MERCI de ne pas le renseigner"
      : "";
  } else return "";
};

export const isRevenuDeclareOk = (
  revenuDeclare?: string,
  salaireNetImposableM1?: string,
  salaireNetImposableM2?: string,
  salaireNetImposableM3?: string,
  candidatOuConjoint?: string
) => {
  const x =
    (salaireNetImposableM1 ? 1 : 0) +
    (salaireNetImposableM2 ? 1 : 0) +
    (salaireNetImposableM3 ? 1 : 0);
  const somme =
    ((salaireNetImposableM1 && Number(salaireNetImposableM1)) || 0) +
    ((salaireNetImposableM2 && Number(salaireNetImposableM2)) || 1) +
    ((salaireNetImposableM3 && Number(salaireNetImposableM3)) || 0);

  // tslint:disable-next-line:no-unused-expression
  const formule =
    revenuDeclare && Math.abs((Number(revenuDeclare) / somme) * (x / 12) - 1);
  const formule2 =
    revenuDeclare && Math.abs((somme / Number(revenuDeclare)) * (12 / x) - 1);

  return x > 0 &&
    revenuDeclare &&
    Number(revenuDeclare) > 3 &&
    ((formule && formule > 0.3) || (formule2 && formule2 > 0.3))
    ? "Nous détectons un écart important entre les revenus  imposables et les salaires actuels" +
        candidatOuConjoint
    : "";
};

export const showRevenusAnnuels = (
  data: ISituationProfessionnellePresentation
) => {
  return (
    data &&
    data.situation &&
    (data.situation === "AUT" ||
      data.situation === "RET" ||
      data.situation === "SPU" ||
      data.situation === "SPR" ||
      data.situation === "TNS" ||
      data.situation === "RCH") &&
    !data.pasAvisImposition
  );
};

export const dossierParticulierToModificationSituationProfessionnelle = (
  dossierParticulier: DossierParticulierDetail,
  situationProConjoint?: DossierParticulierDetail,
  listesProfessionnelles?: SituationProfessionnelle[],
  compositionLocation?: DossierLocationCompositionLocationEnum
) => {
  const {
    qualite,
    nom,
    nomDeNaissance,
    prenom,
    dateNaissance,
    email,
    numeroBatiment,
    typeVoie,
    numero,
    adresse,
    codePostal,
    ville,
    telephonePortable,
    telephoneFixe,
    dateEntree,
    statutOccupation,
    situationMaritale,
    enfants,
    statut,
    refDossierLocataire,
    refDossierLocation,
    ...sp
  } = dossierParticulier;

  let form = {} as SituationProfessionnelleFormData;

  const situation =
    sp.codeStatutProfessionnel &&
    listesProfessionnelles &&
    getSituationProfessionnelleFromCodeStatut(
      sp.codeStatutProfessionnel,
      listesProfessionnelles
    );
  form = {
    principal: {
      ...sp,
      situation: situation || "",
    },
  };

  if (situationProConjoint && compositionLocation === "COUPLE") {
    const {
      qualite,
      nom,
      nomDeNaissance,
      prenom,
      dateNaissance,
      email,
      numeroBatiment,
      typeVoie,
      numero,
      adresse,
      codePostal,
      ville,
      telephonePortable,
      telephoneFixe,
      dateEntree,
      statutOccupation,
      situationMaritale,
      enfants,
      statut,
      refDossierLocataire,
      refDossierLocation,
      ...spConjoint
    } = situationProConjoint;
    const situationConjoint =
      situationProConjoint.codeStatutProfessionnel &&
      listesProfessionnelles &&
      getSituationProfessionnelleFromCodeStatut(
        situationProConjoint.codeStatutProfessionnel,
        listesProfessionnelles
      );
    form = {
      ...form,
      secondaire: {
        ...spConjoint,
        situation: situationConjoint || "",
      },
    };
  }
  return form;
};

// VALIDATORS
